type Coordinate = [number, number];

export type TransitCorridor = {
  kind: 'train' | 'bus';
  serviceId?: string | number;
  floorContext: string;
  path: Coordinate[];
};

// Ignore floating-point interpolation noise without joining nearby parallel paths.
const COORDINATE_DECIMALS = 6;
// Allow short stop connectors to differ, but require almost all of both rides to retrace.
const MIN_SHARED_PATH_RATIO = 0.9;

const coordinateKey = ([lng, lat]: Coordinate) =>
  `${lng.toFixed(COORDINATE_DECIMALS)},${lat.toFixed(COORDINATE_DECIMALS)}`;

const pathEdges = (path: Coordinate[]) => {
  const edges = new Map<string, number>();
  for (let index = 1; index < path.length; index++) {
    const from = path[index - 1];
    const to = path[index];
    const a = coordinateKey(from);
    const b = coordinateKey(to);
    if (a === b) {
      continue;
    }
    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
    const dx = (to[0] - from[0]) * Math.cos((from[1] * Math.PI) / 180);
    const length = Math.hypot(dx, to[1] - from[1]);
    edges.set(key, Math.max(edges.get(key) ?? 0, length));
  }
  return edges;
};

/**
 * Match reversed graph edges, allowing short boarding/alighting connectors to differ.
 * Requires consistent vertex sampling; resampled paths and closed loops are not matched.
 */
export function isReturnOnSameCorridor(first: TransitCorridor, second: TransitCorridor): boolean {
  if (
    first.kind !== second.kind ||
    first.serviceId !== second.serviceId ||
    first.floorContext !== second.floorContext ||
    first.path.length < 2 ||
    second.path.length < 2
  ) {
    return false;
  }
  const aStart = first.path[0];
  const aEnd = first.path.at(-1)!;
  const bStart = second.path[0];
  const bEnd = second.path.at(-1)!;
  // Repeated travel in the same direction is not a round trip.
  if ((aEnd[0] - aStart[0]) * (bEnd[0] - bStart[0]) + (aEnd[1] - aStart[1]) * (bEnd[1] - bStart[1]) >= 0) {
    return false;
  }
  const a = pathEdges(first.path);
  const b = pathEdges(second.path);
  const totalA = [...a.values()].reduce((sum, length) => sum + length, 0);
  const totalB = [...b.values()].reduce((sum, length) => sum + length, 0);
  let shared = 0;
  for (const [edge, length] of a) {
    shared += Math.min(length, b.get(edge) ?? 0);
  }
  // Both rides must substantially retrace the same path, not merely cross or share a stop.
  return (
    totalA > 0 && totalB > 0 && shared / totalA >= MIN_SHARED_PATH_RATIO && shared / totalB >= MIN_SHARED_PATH_RATIO
  );
}
